/* eslint-disable react/jsx-max-props-per-line */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Chip, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { FilterChipBarV2, TableV2 } from '../../../components/v2';
import {
  LENTE_LOOKUP_CHIP,
  LENTE_LOOKUP_CHIP_OPTIONS,
  filterLenteLookupRows,
  formatAdereLine,
  formatMedia,
  getItemMedia,
  getItemPendentes,
  getMetaDiaria,
  getUnidadeLabel,
  openDigitadorDrill,
} from './commandCenterConstants';

const ritmoChipSx = {
  height: 20,
  fontSize: '0.65rem',
  '& .MuiChip-label': { px: 0.75 },
};

const LenteLookupTable = ({
  prodItems,
  cargaItems,
  lenteId,
  loading,
  adereResumo,
}) => {
  const [q, setQ] = useState('');
  const [chip, setChip] = useState(LENTE_LOOKUP_CHIP.TODOS);
  const meta = getMetaDiaria(lenteId);
  const unidadeShort = getUnidadeLabel(lenteId) === 'postes' ? 'postes' : 'serv.';

  const adereById = useMemo(() => {
    const map = {};
    (adereResumo?.digitadores || []).forEach((item) => {
      if (item.digitadorId && item.adere) map[item.digitadorId] = item.adere;
    });
    return map;
  }, [adereResumo]);

  const rows = useMemo(() => {
    const cargaById = new Map((cargaItems || []).map((item) => [item.digitadorId, item]));
    return (prodItems || []).map((item) => {
      const carga = cargaById.get(item.digitadorId);
      const media = getItemMedia(item);
      const pendentes = carga ? getItemPendentes(carga) : 0;
      const abaixoMeta = meta != null && media != null && media < meta;
      const digitados = carga?.servicosDigitados ?? 0;
      const totalVol = carga?.servicosTotal ?? item.total ?? 0;
      const adere = adereById[item.digitadorId] || null;
      return {
        id: item.digitadorId,
        digitadorId: item.digitadorId,
        nome: item.digitadorNome || '—',
        mediaDiaUtil: media,
        pendentes,
        nsirs: carga?.nsirs ?? 0,
        digitados,
        totalVol,
        abaixoMeta,
        adereLine: formatAdereLine(adere),
      };
    }).sort((a, b) => (b.mediaDiaUtil ?? 0) - (a.mediaDiaUtil ?? 0));
  }, [prodItems, cargaItems, meta, adereById]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    const byChip = filterLenteLookupRows(rows, chip);
    if (!query) return byChip;
    return byChip.filter((row) => (row.nome || '').toLowerCase().includes(query));
  }, [rows, chip, q]);

  const filterActive = Boolean(q) || chip !== LENTE_LOOKUP_CHIP.TODOS;

  const chipGroups = useMemo(() => [
    {
      label: 'Mostrar',
      chips: LENTE_LOOKUP_CHIP_OPTIONS.map((opt) => ({
        id: opt.id,
        label: opt.label,
        selected: chip === opt.id,
        onClick: () => {
          if (opt.id === LENTE_LOOKUP_CHIP.TODOS) {
            setChip(LENTE_LOOKUP_CHIP.TODOS);
            return;
          }
          setChip((prev) => (prev === opt.id ? LENTE_LOOKUP_CHIP.TODOS : opt.id));
        },
      })),
    },
  ], [chip]);

  const columns = [
    {
      field: 'nome',
      headerName: 'Digitador',
      minWidth: 160,
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={params.row.abaixoMeta ? 700 : 500} noWrap>
          {params.row.nome}
        </Typography>
      ),
    },
    {
      field: 'ritmo',
      headerName: 'Ritmo',
      width: 110,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.row.abaixoMeta ? 'Abaixo' : 'No ritmo'}
          size="small"
          color={params.row.abaixoMeta ? 'warning' : 'success'}
          variant="outlined"
          sx={ritmoChipSx}
        />
      ),
    },
    {
      field: 'mediaDiaUtil',
      headerName: 'Média/dia',
      width: 100,
      renderCell: (params) => (
        meta != null
          ? `${formatMedia(params.row.mediaDiaUtil)} / ${meta}`
          : formatMedia(params.row.mediaDiaUtil)
      ),
    },
    {
      field: 'nsirs',
      headerName: 'NSIRs',
      width: 80,
    },
    {
      field: 'volume',
      headerName: unidadeShort === 'postes' ? 'Postes' : 'Serviços',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        `${Number(params.row.digitados || 0).toLocaleString('pt-BR')}/${Number(params.row.totalVol || 0).toLocaleString('pt-BR')} ${unidadeShort}`
      ),
    },
    {
      field: 'pendentes',
      headerName: 'Pendentes',
      width: 100,
      renderCell: (params) => Number(params.row.pendentes || 0).toLocaleString('pt-BR'),
    },
    {
      field: 'adere',
      headerName: 'Adere',
      minWidth: 180,
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Typography variant="caption" color="text.secondary" noWrap>
          {params.row.adereLine || '—'}
        </Typography>
      ),
    },
    {
      field: 'acao',
      headerName: 'Ação',
      width: 148,
      minWidth: 148,
      maxWidth: 148,
      sortable: false,
      disableColumnMenu: true,
      resizable: false,
      renderCell: (params) => (
        <Button
          size="small"
          endIcon={<OpenInNewIcon fontSize="small" />}
          onClick={() => openDigitadorDrill({
            digitadorId: params.row.digitadorId,
            nome: params.row.nome,
          })}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Visualizar
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1, pr: 2 }}>
        Lista da lente
      </Typography>
      <Box sx={{ mb: 1.5 }}>
        <FilterChipBarV2
          searchLabel="Digitador"
          searchValue={q}
          onSearchChange={setQ}
          searchPlaceholder="Nome do digitador"
          chipGroups={chipGroups}
          chipGroupsLayout="search-row"
          filterActive={filterActive}
          filteredCount={filtered.length}
          totalCount={rows.length}
          countLabel="digitadores"
        />
      </Box>
      <TableV2
        rows={filtered}
        loading={loading}
        columns={columns}
        autoHeight
        disablePagination
        rowHeight={52}
        emptyMessage="Nenhum digitador nesta lente"
      />
    </Box>
  );
};

LenteLookupTable.propTypes = {
  prodItems: PropTypes.arrayOf(PropTypes.object),
  cargaItems: PropTypes.arrayOf(PropTypes.object),
  lenteId: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  adereResumo: PropTypes.object,
};

LenteLookupTable.defaultProps = {
  prodItems: [],
  cargaItems: [],
  loading: false,
  adereResumo: null,
};

export default LenteLookupTable;
